import React from "react";
import { Modal, Button, Space, Typography, Badge } from "antd";
import { PlayCircleOutlined, CrownOutlined, EyeOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import store from "../store";
import { getWebSocket } from "../Socket";

const { Title, Text } = Typography;

interface HandSelectionModalState {
  availableHands: string[];
  canPickHand: boolean;
  isVisible: boolean;
  isPreviewMode: boolean;
}

interface HandSelectionModalProps {
  visible: boolean;
  onClose: () => void;
  previewMode?: boolean;
  t: any;
}

class HandSelectionModalComponent extends React.Component<HandSelectionModalProps, HandSelectionModalState> {
  constructor(props: HandSelectionModalProps) {
    super(props);
    this.state = {
      availableHands: [],
      canPickHand: false,
      isVisible: false,
      isPreviewMode: props.previewMode || false
    }
  }

  componentDidMount(): void {
    store.subscribe(() => {
      let state = store.getState();
      this.setState({
        availableHands: this.getAvailableHands(state),
        canPickHand: this.getCanPickHand(state)
      })
    })
  }

  getAvailableHands(state: any): string[] {
    return state.kingGame.gameState.availableHands || [];
  }

  getCanPickHand(state: any): boolean {
    const gameState = state.kingGame.gameState;
    return gameState.currentHand == null && gameState.playerId === gameState.currentPlayer;
  }

  groupHandsByCount(hands: string[]): { hand: string; count: number }[] {
    const handCounts = hands.reduce((acc, hand) => {
      acc[hand] = (acc[hand] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    return Object.entries(handCounts).map(([hand, count]) => ({ hand, count }));
  }

  pickHand = (hand: string): void => {
    if (!this.state.canPickHand || this.props.previewMode) return;

    let data = {
      "id": "chooseHand",
      "hand": hand
    };
    const ws = getWebSocket();
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify(data));
    }
    this.props.onClose();
  }

  getLocalizedHandName = (hand: string): string => {
    const { t } = this.props;
    // Try to get localized name from translations
    const translationKey = `hands.handNames.${hand}`;
    const localizedName = t(translationKey, { defaultValue: null });
    
    // If translation exists and is not the same as the key, use it
    if (localizedName && localizedName !== translationKey) {
      return localizedName;
    }

    // Handle dynamic trick numbers (e.g., "5 tricks" -> "5_tricks")
    if (hand.toLowerCase().includes('trick')) {
      const trickMatch = hand.match(/(\d+)/);
      if (trickMatch) {
        const number = trickMatch[1];
        const tricksKey = `hands.handNames.${number}_tricks`;
        const tricksTranslation = t(tricksKey, { defaultValue: null });
        if (tricksTranslation && tricksTranslation !== tricksKey) {
          return tricksTranslation;
        }
      }
    }

    // Return original if no translation found
    return hand;
  }

  render() {
    const { visible, onClose, previewMode } = this.props;
    const { availableHands, canPickHand } = this.state;

    const title = previewMode ? (
      <Space size="small">
        <EyeOutlined style={{ color: 'var(--secondary-color)', fontSize: '16px' }} />
        <Title level={5} style={{ margin: 0, color: 'var(--text-primary)', fontSize: '16px' }}>
          Available Hands
        </Title>
      </Space>
    ) : (
      <Space size="small">
        <CrownOutlined style={{ color: 'var(--primary-color)', fontSize: '16px' }} />
        <Title level={5} style={{ margin: 0, color: 'var(--text-primary)', fontSize: '16px' }}>
          {this.props.t('hands.chooseHand')}
        </Title>
      </Space>
    );

    return (
      <Modal
        title={title}
        open={visible}
        onCancel={onClose}
        footer={null}
        centered
        width={320}
        className="hand-selection-modal"
        styles={{
          content: {
            background: 'rgba(30, 41, 59, 0.95)',
            backdropFilter: 'blur(20px)',
            border: '1px solid var(--border-color)',
            borderRadius: 'var(--radius-md)',
            margin: '16px'
          },
          header: {
            borderBottom: '1px solid var(--border-color)',
            padding: '12px 16px 8px 16px'
          },
          body: {
            padding: '12px 16px'
          }
        }}
      >
        {/* Action Prompt */}
        {canPickHand && !previewMode && (
          <div style={{ 
            marginBottom: 12, 
            padding: 8, 
            background: 'var(--primary-color)', 
            borderRadius: 'var(--radius-sm)',
            textAlign: 'center'
          }}>
            <Text style={{ 
              color: 'white', 
              fontWeight: 500, 
              fontSize: '0.8rem'
            }}>
              {this.props.t('hands.yourTurn')}
            </Text>
          </div>
        )}



        {/* Hands Grid */}
        <div style={{ marginBottom: 12 }}>
          {availableHands.length > 0 ? (
            <div style={{ display: 'flex', gap: '8px' }}>
              {/* Positive Hands */}
              {(() => {
                const positiveHands = availableHands.filter(hand => !['rifki', 'no_hands', 'no_men', 'no_women', 'no_hearts', 'no_tricks', 'last_two'].includes(hand.toLowerCase()));
                const groupedPositiveHands = this.groupHandsByCount(positiveHands);
                return groupedPositiveHands.length > 0 && (
                  <div style={{ flex: 1 }}>
                    <Space direction="vertical" size={3} style={{ width: '100%' }}>
                      {groupedPositiveHands.map(({ hand, count }) => {
                        const isSelectable = canPickHand && !previewMode;
                        return (
                          <Badge
                            key={hand}
                            count={count > 1 ? count : 0}
                            offset={[-8, 8]}
                            size="small"
                            style={{ 
                              backgroundColor: 'rgba(34, 197, 94, 0.8)',
                              color: 'white',
                              fontSize: '10px',
                              fontWeight: 'bold'
                            }}
                          >
                            <Button
                              type="default"
                              size="small"
                              onClick={() => this.pickHand(hand)}
                              disabled={!isSelectable}
                              style={{
                                width: '100%',
                                height: '32px',
                                background: isSelectable 
                                  ? 'rgba(34, 197, 94, 0.1)'
                                  : 'var(--bg-secondary)',
                                border: isSelectable 
                                  ? '1px solid rgba(34, 197, 94, 0.3)' 
                                  : '1px solid var(--border-color)',
                                borderRadius: 'var(--radius-sm)',
                                color: isSelectable ? 'rgb(34, 197, 94)' : 'var(--text-muted)',
                                fontWeight: 500,
                                fontSize: '0.8rem',
                                transition: 'all var(--transition-normal)',
                                cursor: isSelectable ? 'pointer' : 'not-allowed',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                padding: '0 8px'
                              }}
                              onMouseEnter={(e) => {
                                if (isSelectable) {
                                  e.currentTarget.style.background = 'rgba(34, 197, 94, 0.2)';
                                  e.currentTarget.style.borderColor = 'rgba(34, 197, 94, 0.5)';
                                }
                              }}
                              onMouseLeave={(e) => {
                                if (isSelectable) {
                                  e.currentTarget.style.background = 'rgba(34, 197, 94, 0.1)';
                                  e.currentTarget.style.borderColor = 'rgba(34, 197, 94, 0.3)';
                                }
                              }}
                            >
                              {this.getLocalizedHandName(hand)}
                            </Button>
                          </Badge>
                        );
                      })}
                    </Space>
                  </div>
                );
              })()}

              {/* Punishment Hands */}
              {(() => {
                const punishmentHands = availableHands.filter(hand => ['rifki', 'no_hands', 'no_men', 'no_women', 'no_hearts', 'no_tricks', 'last_two'].includes(hand.toLowerCase()));
                const groupedPunishmentHands = this.groupHandsByCount(punishmentHands);
                return groupedPunishmentHands.length > 0 && (
                  <div style={{ flex: 1 }}>
                    <Space direction="vertical" size={3} style={{ width: '100%' }}>
                      {groupedPunishmentHands.map(({ hand, count }) => {
                        const isSelectable = canPickHand && !previewMode;
                        return (
                          <Badge
                            key={hand}
                            count={count > 1 ? count : 0}
                            offset={[-8, 8]}
                            size="small"
                            style={{ 
                              backgroundColor: 'rgba(239, 68, 68, 0.8)',
                              color: 'white',
                              fontSize: '10px',
                              fontWeight: 'bold'
                            }}
                          >
                            <Button
                              type="default"
                              size="small"
                              onClick={() => this.pickHand(hand)}
                              disabled={!isSelectable}
                              style={{
                                width: '100%',
                                height: '32px',
                                background: isSelectable 
                                  ? 'rgba(239, 68, 68, 0.1)'
                                  : 'var(--bg-secondary)',
                                border: isSelectable 
                                  ? '1px solid rgba(239, 68, 68, 0.3)' 
                                  : '1px solid var(--border-color)',
                                borderRadius: 'var(--radius-sm)',
                                color: isSelectable ? 'rgb(239, 68, 68)' : 'var(--text-muted)',
                                fontWeight: 500,
                                fontSize: '0.8rem',
                                transition: 'all var(--transition-normal)',
                                cursor: isSelectable ? 'pointer' : 'not-allowed',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                padding: '0 8px'
                              }}
                              onMouseEnter={(e) => {
                                if (isSelectable) {
                                  e.currentTarget.style.background = 'rgba(239, 68, 68, 0.2)';
                                  e.currentTarget.style.borderColor = 'rgba(239, 68, 68, 0.5)';
                                }
                              }}
                              onMouseLeave={(e) => {
                                if (isSelectable) {
                                  e.currentTarget.style.background = 'rgba(239, 68, 68, 0.1)';
                                  e.currentTarget.style.borderColor = 'rgba(239, 68, 68, 0.3)';
                                }
                              }}
                            >
                              {this.getLocalizedHandName(hand)}
                            </Button>
                          </Badge>
                        );
                      })}
                    </Space>
                  </div>
                );
              })()}
            </div>
          ) : (
            <div style={{ 
              textAlign: 'center', 
              padding: '16px 12px',
              color: 'var(--text-muted)',
              background: 'var(--bg-tertiary)',
              borderRadius: 'var(--radius-sm)',
              border: '1px solid var(--border-color)'
            }}>
              <PlayCircleOutlined style={{ 
                fontSize: '1.5rem', 
                marginBottom: 6, 
                color: 'var(--text-muted)' 
              }} />
              <Text style={{ 
                display: 'block', 
                fontSize: '0.8rem',
                color: 'var(--text-secondary)'
              }}>
                No hands yet
              </Text>
            </div>
          )}
        </div>

        {/* Waiting State */}
        {!canPickHand && availableHands.length > 0 && !previewMode && (
          <div style={{ 
            padding: 8, 
            background: 'var(--bg-tertiary)', 
            borderRadius: 'var(--radius-sm)',
            textAlign: 'center',
            border: '1px solid var(--border-color)',
            marginBottom: 12
          }}>
            <Text style={{ 
              color: 'var(--text-secondary)', 
              fontSize: '0.75rem'
            }}>
              {this.props.t('hands.waitingForTurnDots')}
            </Text>
          </div>
        )}

      </Modal>
    );
  }
}

// Functional wrapper component to use hooks
const HandSelectionModal = ({ visible, onClose, previewMode }: {
  visible: boolean;
  onClose: () => void;
  previewMode?: boolean;
}) => {
  const { t } = useTranslation();
  return (
    <HandSelectionModalComponent
      visible={visible}
      onClose={onClose}
      previewMode={previewMode}
      t={t}
    />
  );
};

export default HandSelectionModal;